import { Triangle } from "lucide-react";
import { ENTITY_FULL_NAME } from "../../../../api/config";

/**
 * MentionText — Parses text for @mentions and renders them as styled chips.
 *
 * Detects @DisplayName patterns and renders them as inline sky-blue
 * pill-styled spans with the user's initial icon.
 *
 * Usage:
 *   <MentionText text={msg.content} currentUserUuid={userUuid} members={members} />
 */

const MENTION_REGEX = /@(\w[\w\s]*?)(?=\s|$|[.,!?;:])/g;

const MentionText = ({ text, currentUserUuid, members = {} }) => {
  if (!text) return null;

  // Build a lookup of display names → uuid for highlighting self-mentions
  const nameToUuid = {};
  for (const [uuid, member] of Object.entries(members)) {
    const name = member.display_name || member.first_name;
    if (name) nameToUuid[name.toLowerCase()] = uuid;
  }
  // Register the AI assistant so its @mention is always recognized
  nameToUuid[ENTITY_FULL_NAME.toLowerCase()] = "ai_assistant";

  const parts = [];
  let lastIndex = 0;
  let match;

  // Reset regex
  MENTION_REGEX.lastIndex = 0;

  while ((match = MENTION_REGEX.exec(text)) !== null) {
    const mentionName = match[1];
    const start = match.index;

    // Push text before the mention
    if (start > lastIndex) {
      parts.push({
        type: "text",
        content: text.slice(lastIndex, start),
        key: `t-${lastIndex}`,
      });
    }

    // Check if this mention matches a known member
    const matchedUuid = nameToUuid[mentionName.toLowerCase()];
    const isSelf = matchedUuid === currentUserUuid;
    const isAI = matchedUuid === "ai_assistant";

    parts.push({
      type: "mention",
      name: mentionName,
      uuid: matchedUuid,
      isSelf,
      isAI,
      key: `m-${start}`,
    });

    lastIndex = start + match[0].length;
  }

  // Push remaining text
  if (lastIndex < text.length) {
    parts.push({
      type: "text",
      content: text.slice(lastIndex),
      key: `t-${lastIndex}`,
    });
  }

  // If no mentions found, return plain text
  if (parts.length === 0 || (parts.length === 1 && parts[0].type === "text")) {
    return null; // signal to caller to use default rendering
  }

  return (
    <span>
      {parts.map((part) => {
        if (part.type === "text") {
          return <span key={part.key}>{part.content}</span>;
        }

        const initial = (part.name[0] || "?").toUpperCase();

        return (
          <span
            key={part.key}
            className={[
              "inline-flex items-center gap-x-0.5 px-1 py-px rounded text-[12.5px] font-medium align-baseline mx-0.5",
              part.isAI
                ? "bg-[#0EA5E9]/10 text-[#0EA5E9]"
                : part.isSelf
                  ? "bg-sky-100 dark:bg-sky-900/30 text-sky-700 dark:text-sky-300"
                  : "bg-violet-50 dark:bg-violet-900/20 text-violet-700 dark:text-violet-300",
            ].join(" ")}
          >
            {part.isAI ? (
              <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-[#0EA5E9]/15 shrink-0">
                <Triangle
                  className="h-2.5 w-2.5"
                  fill="#0EA5E9"
                  strokeWidth={0}
                />
              </span>
            ) : (
              <span
                className={[
                  "inline-flex items-center justify-center w-4 h-4 rounded-full text-[9px] font-semibold text-white shrink-0",
                  part.isSelf ? "bg-sky-500" : "bg-violet-500",
                ].join(" ")}
              >
                {initial}
              </span>
            )}
            <span>{part.isAI ? ENTITY_FULL_NAME : part.name}</span>
          </span>
        );
      })}
    </span>
  );
};

export default MentionText;
